import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemText,
  Link,
  Divider,
  Chip,
  Stack,
  Alert,
} from '@mui/material';
import MenuBookIcon from '@mui/icons-material/MenuBook';

/**
 * NC-1103 (Feat 2.0, Phase 5): Portal-Hilfeseiten.
 *
 * Zwei Schritt-für-Schritt-Guides für die Endabnahme-Prozesse — „Schulung
 * anlegen" (Tutor/Operator) und „Workload anlegen" (Admin). Bewusst
 * **statisch**: die Seite macht KEINE API-Calls (viewer-safe, NC-1103 Edge 1)
 * und funktioniert ohne Screenshots/Assets (Edge 3). Deep-Links auf einzelne
 * Abschnitte via `/hilfe#<anker>` werden beim Mount angesprungen (Edge 2).
 */

interface GuideStep {
  title: string;
  points: string[];
}

interface Guide {
  id: string;
  title: string;
  audience: string;
  intro: string;
  steps: GuideStep[];
}

const GUIDES: Guide[] = [
  {
    id: 'schulung-anlegen',
    title: 'So lege ich eine Schulung an',
    audience: 'Tutor / Operator',
    intro:
      'Der häufigste Ablauf: von der Schulungs-Anlage über die Provisionierung bis zur Teilnehmer-Verteilung und dem Einsammeln der Projektdateien.',
    steps: [
      {
        title: '1. Schulung anlegen',
        points: [
          'Unter „Schulungsläufe" → „Neue Schulung" den Workload wählen: Standard (z. B. aec, Standard_D8s_v5) oder Premium/GPU (z. B. aec-gpu, A10) — der Unterschied ist die Grafik-Leistung der Teilnehmer-VMs.',
          'Kostenstelle auswählen (Pflicht — ohne aktive Kostenstelle wird die Anlage abgelehnt).',
          'Schulungstermin (Beginn/Ende) und Teilnehmer eintragen. Der Termin steuert das Zugriffsfenster der Teilnehmerseite.',
        ],
      },
      {
        title: '2. Unterlagen-Vorlage prüfen / ergänzen',
        points: [
          'Im Workload-Editor → Tab „Schulungsunterlagen" die Ordnerstruktur prüfen: Ordner anlegen und Dateien (PDF, Projektdateien, dwg/rvt …) hochladen.',
          'Die Struktur ist eine Vorlage: sie wird bei der Provisionierung nach C:\\Schulungsunterlagen jeder VM ausgerollt. Leere Ordner bleiben als Struktur erhalten.',
        ],
      },
      {
        title: '3. Provisionieren',
        points: [
          'In der Schulungs-Detailseite „Provisionierung starten" — es entstehen Ressourcengruppe, AD-Benutzer und VMs (ca. 20 Min, bei mehr als 5 Teilnehmern länger — die VMs werden nacheinander angelegt).',
          'Der Fortschritt ist über den verlinkten GitHub-Actions-Job einsehbar; bei Fehlern erscheint eine rote Meldung mit Log-Link.',
        ],
      },
      {
        title: '4. Teilnehmer-Links verteilen',
        points: [
          'In der Detailseite unter „Teilnehmer-Zugang" → „Teilnehmer-Links erzeugen". Die Links werden nur EINMAL angezeigt (nur ein Hash wird gespeichert) — jetzt kopieren oder über die Druckansicht ausgeben.',
          'Jeder Teilnehmer öffnet seinen Link (gültig 5 Tage vor Beginn bis 7 Tage nach Ende) und findet dort Zugangsdaten und die RDP-Datei.',
        ],
      },
      {
        title: '5. Während der Schulung Dateien nachschieben',
        points: [
          'In der Detailseite → Sektion „Unterlagen (dieser Lauf)" lauf-spezifische Dateien hochladen.',
          '„Jetzt auf VMs verteilen" rollt Vorlage + Lauf-Overlay erneut auf die laufenden VMs aus (nur bei aktivem Lauf, 5-Minuten-Sperre gegen Doppelklick).',
        ],
      },
      {
        title: '6. Projektdateien einsammeln',
        points: [
          '„Projektdateien einsammeln" kopiert C:\\Schulungsunterlagen\\Projektdateien aller VMs in den 7-Tage-Downloadbereich; das geschieht auch automatisch vor dem Cleanup.',
          'Die eingesammelten Zips erscheinen unter „Eingesammelte anzeigen" mit Tutor-Download.',
        ],
      },
      {
        title: '7. Cleanup',
        points: [
          'Am Ende die VMs stoppen (Kosten beenden) oder die Schulung löschen — Ressourcen, AD-Benutzer und KeyVault-Secrets werden entfernt; der Cosmos-Eintrag bleibt als Audit-Spur.',
        ],
      },
      {
        title: 'Was sieht der Teilnehmer?',
        points: [
          'Vorperiode (bis Beginn): Kursinfos, RDP-Datei zum Download und Zugangsdaten.',
          'Während der Schulung: dito, plus Hinweis auf C:\\Schulungsunterlagen.',
          'Nachperiode (bis 7 Tage nach Ende): Download der eigenen Projektdateien mit Countdown.',
        ],
      },
    ],
  },
  {
    id: 'workload-anlegen',
    title: 'So legt man einen Workload an',
    audience: 'Admin',
    intro:
      'Seltener, aber grundlegend: ein neuer Workload (Software-Stack) von der Anlage über den Image-Build bis zur Test-Schulung.',
    steps: [
      {
        title: '1. Workload im Portal anlegen',
        points: [
          'Unter „Workloads" → „Neuer Workload". Felder: Anwendungen mit installOrder und silentArgs (stille Installations-Parameter), packerConfig (Build-VM-Größe und Basis-Image) und optional runtimeConfig für GPU-Läufe.',
          'Kostenstelle (ownerCostCenter) ist Pflicht — sie taggt die Build-Kosten.',
          'Wichtig: Build-Größe (packerConfig.vmSize) ≠ Laufzeit-Größe der Schulungs-VMs.',
        ],
      },
      {
        title: '2. Installer hochladen + verknüpfen',
        points: [
          'Unter „Installers" die Installationsdateien hochladen und mit den Anwendungen des Workloads verknüpfen. Große Installer laden bei der Installation ggf. weitere Dateien aus dem Internet nach.',
        ],
      },
      {
        title: '3. Unterlagen-Vorlage anlegen',
        points: [
          'Im Workload-Editor → Tab „Schulungsunterlagen" die Standard-Ordnerstruktur und Dateien hinterlegen, die jede Schulung dieses Workloads erhalten soll.',
        ],
      },
      {
        title: '4. Image bauen',
        points: [
          'Den Image-Build starten und den Build-Status verfolgen (Build-Logs). Ein Build kann je nach Software 1–3 Stunden dauern.',
        ],
      },
      {
        title: '5. Publizieren / promoten',
        points: [
          'Die erfolgreiche Image-Version in die Compute Gallery publizieren/promoten, damit neue Schulungen sie verwenden.',
        ],
      },
      {
        title: '6. Test-Schulung fahren',
        points: [
          'Eine Test-Schulung mit dem neuen Workload anlegen und einmal komplett durchlaufen — siehe Guide „So lege ich eine Schulung an".',
        ],
      },
    ],
  },
];

export default function HelpPage() {
  const location = useLocation();

  // NC-1103 Edge 2: deep-link support — jump to the anchored section on mount
  // (and whenever the hash changes). Guarded so a missing/unknown anchor is a
  // no-op instead of a crash.
  useEffect(() => {
    const hash = location.hash?.replace(/^#/, '');
    if (!hash) return;
    const el = document.getElementById(hash);
    // Guard: unknown anchor → no-op; scrollIntoView is absent in jsdom.
    if (el && typeof el.scrollIntoView === 'function') {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [location.hash]);

  return (
    <Box data-testid="help-page" sx={{ maxWidth: 900, mx: 'auto' }}>
      <Stack direction="row" spacing={1} alignItems="center" mb={1}>
        <MenuBookIcon color="primary" />
        <Typography variant="h4">Hilfe &amp; Anleitungen</Typography>
      </Stack>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Schritt-für-Schritt-Anleitungen für die beiden zentralen Abläufe im Portal.
      </Typography>

      {/* Inhaltsverzeichnis mit Anker-Links (Deep-Link-fähig) */}
      <Paper variant="outlined" sx={{ p: 2, mb: 4 }}>
        <Typography variant="h6" gutterBottom>
          Inhalt
        </Typography>
        <List dense>
          {GUIDES.map((g) => (
            <ListItem key={g.id} disableGutters>
              <ListItemText
                primary={
                  <Link href={`#${g.id}`} underline="hover">
                    {g.title}
                  </Link>
                }
                secondary={`Für: ${g.audience}`}
              />
            </ListItem>
          ))}
        </List>
      </Paper>

      {GUIDES.map((guide) => (
        <Box key={guide.id} component="section" sx={{ mb: 5 }}>
          {/* Raw anchor element carrying the id — a plain <div> always keeps its
              id in the DOM (independent of any component-lib prop forwarding),
              so deep-links + getElementById resolve reliably. scroll-margin-top
              keeps the target clear of the sticky app bar. */}
          <div id={guide.id} style={{ scrollMarginTop: '80px' }} />
          <Typography variant="h5" gutterBottom>
            {guide.title}
          </Typography>
          <Chip size="small" label={guide.audience} color="primary" variant="outlined" sx={{ mb: 1 }} />
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {guide.intro}
          </Typography>
          <Divider sx={{ mb: 2 }} />

          <Stack spacing={2}>
            {guide.steps.map((step, idx) => (
              <Paper key={idx} variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                  {step.title}
                </Typography>
                <List dense sx={{ listStyleType: 'disc', pl: 3 }}>
                  {step.points.map((point, pIdx) => (
                    <ListItem key={pIdx} sx={{ display: 'list-item', py: 0.25, pl: 0 }}>
                      <ListItemText primary={point} />
                    </ListItem>
                  ))}
                </List>
              </Paper>
            ))}
          </Stack>
        </Box>
      ))}

      <Alert severity="info" sx={{ mt: 2 }}>
        Etwas fehlt oder stimmt nicht? Melden Sie es über „Feedback &amp; Fehler" — die
        Anleitungen werden laufend anhand echter Durchläufe verfeinert.
      </Alert>
    </Box>
  );
}
